import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  ModalProps,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Text
} from "@mantine/core";
import {
  DispatchNotificationBatchCreateRequest,
  DispatchNotificationBatchCreateResponse,
  DispatchNotificationOverrideFlags,
  DispatchNotificationPreviewResponse,
  extractErrorMessage,
  useCreateDispatchNotificationsMutation,
  useLazyPreviewDispatchNotificationsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {
  DispatchNotificationDecisionBadge, DispatchNotificationIcon,
  DispatchNotificationOverridesPanel,
  DispatchNotificationSkipReason,
  DispatchNotificationStatusBadge,
  DispatchNotificationSummaryBar
} from "./DispatchNotifyModal";
import {IconCircleCheck, IconCircleCheckFilled, IconCircleX, IconCircleXFilled} from "@tabler/icons-react";
import {useAutoCloseOnSuccess} from "../useAutoCloseOnSuccess";

export type DispatchBatchNotifyModalProps = ModalProps & {
  dispatchIds: string[]
}

export const DispatchBatchNotifyModal = (props: DispatchBatchNotifyModalProps) => {
  const {dispatchIds, onClose, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [step, setStep] = useState<"preview" | "result">("preview");
  const [override, setOverride] = useState<DispatchNotificationOverrideFlags>({cooldown: false, quiteHours: true});

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [preview, setPreview] = useState<DispatchNotificationPreviewResponse | undefined>(undefined);
  const [result, setResult] = useState<DispatchNotificationBatchCreateResponse | undefined>(undefined);

  const [triggerPreview] = useLazyPreviewDispatchNotificationsQuery();
  const [createDispatchNotifications] = useCreateDispatchNotificationsMutation()

  useEffect(() => {
    setStep("preview")
    setLoadingPreview(false)
    setLoadingSend(false)
    setPreview(undefined)
    setResult(undefined)
  }, [dispatchIds.join(",")])

  const _onClose = useCallback(() => {
    onClose()
    setStep("preview")
    setLoadingPreview(false)
    setLoadingSend(false)
    setPreview(undefined)
    setResult(undefined)
  }, [onClose])

  const runPreview = useCallback(() => {
    setLoadingPreview(true);
    setError(undefined);

    triggerPreview({dispatchIds: dispatchIds})
      .unwrap()
      .then((payload) => {
        setPreview(payload);
      })
      .catch((err) => {
        setError(extractErrorMessage(err));
      })
      .finally(() => {
        setLoadingPreview(false);
      });

  }, [triggerPreview, dispatchIds])

  const send = useCallback(() => {
    setLoadingSend(true);
    setError(undefined);

    const request: DispatchNotificationBatchCreateRequest = {
      dispatchIds: dispatchIds,
      overrideCooldown: override.cooldown,
      overrideQuiteHours: override.quiteHours,
    }

    createDispatchNotifications(request)
      .unwrap()
      .then((payload) => {
        setResult(payload);
        setStep("result");
      })
      .catch((err) => {
        setError(extractErrorMessage(err));
      })
      .finally(() => {
        setLoadingSend(false);
      });

  }, [createDispatchNotifications, override, dispatchIds])

  useEffect(() => {
    if (!preview && step === 'preview') {
      runPreview()
    }
  }, [dispatchIds, step, runPreview, preview])

  const queued = result?.queued ?? 0;
  const skipped = result?.skipped ?? 0;
  const failed = result?.failed ?? 0;

  const autoCloseEnabled = queued > 0 && skipped === 0 && failed === 0;

  const { remainingMs, progress } = useAutoCloseOnSuccess({
    enabled: autoCloseEnabled,
    seconds: 4,
    onClose: _onClose,
  });

  return (
    <Modal {...rest} onClose={_onClose}>
      <Stack spacing="md">
        {error && (
          <Alert title={t("common.error.title", "Error")} color="red" mb="md">
            {error}
          </Alert>
        )}

        <Text size="sm" color="dimmed">
          Send notifications to drivers for the selected dispatches.
          Each notification will be evaluated against system safeguards such as cooldowns, quiet hours, and rate limits.
          Some notifications may be skipped if they do not meet delivery conditions.
        </Text>

        {step === "preview" && (
          <>
            <Group position="apart">
              <Text fw={600}>
                {t("common.entity.dispatchnotification.modal.preview", "Preview")}
              </Text>
              <Group spacing="xs">
                <Button variant="subtle" onClick={runPreview} disabled={loadingPreview}>
                  {t("common.action.refresh", "Refresh")}
                </Button>
              </Group>
            </Group>

            <DispatchNotificationOverridesPanel value={override} onChange={setOverride}/>

            {loadingPreview ? (
              <Group position="center" py="md"><Loader/></Group>
            ) : (
              <PreviewByDispatchTable preview={preview}/>
            )}

            <Group position="apart" mt="sm">
              <Button variant="default" onClick={_onClose}>
                {t("common.action.cancel", "Cancel")}
              </Button>
              <Button onClick={send} loading={loadingSend}>
                {t("common.action.sendnow", "Send now")}
              </Button>
            </Group>
          </>
        )}

        {step === "result" && (
          <>
            <Group position="apart">
              <Text fw={600}>Result</Text>
              <Button variant="subtle" onClick={() => setStep("preview")}>
                {t("common.entity.dispatchnotification.modal.action.backtopreview", "Back to preview")}
              </Button>
            </Group>

            <DispatchNotificationSummaryBar
              queued={result?.queued ?? 0}
              skipped={result?.skipped ?? 0}
              failed={result?.failed ?? 0}
            />

            <ScrollArea h={280}>
              <BatchResultsTable result={result}/>
            </ScrollArea>

            <Group position="right" mt="sm">
              <Button onClick={_onClose}>
                {t("common.action.done", "Done")}
              </Button>
            </Group>
          </>
        )}

        {autoCloseEnabled && (
          <Group position="apart" mt="sm">
            <Text size="sm" color="dimmed">
              Closing in {Math.ceil(remainingMs / 1000)}s…
            </Text>
            <Progress value={progress} w={180} />
          </Group>
        )}

      </Stack>
    </Modal>
  )
}

const PreviewByDispatchTable = ({preview}: { preview: DispatchNotificationPreviewResponse }) => {

  const {t} = useTranslation(["common"]);

  return (
    <Table withBorder highlightOnHover>
      <thead>
      <tr>
        <th>{t("common.entity.dispatchnotification.driver", "Driver")}</th>
        <th>{t("common.entity.dispatchnotification.decision", "Decision")}</th>
        <th>{t("common.entity.dispatchnotification.reason", "Reason")}</th>
        <th>{t("common.entity.dispatchnotification.nextallowed", "Next allowed")}</th>
      </tr>
      </thead>
      <tbody>
      {preview && preview.results.length > 0 ? (
        <>
          {preview.results.map(({dispatchId, decision}, i) => (
            <tr key={`${dispatchId}-${i}`}>
              <td>
                <Text size="sm">
                  {decision.driverId ? <DefaultText value={decision.driverName}/> : "-"}
                </Text>
              </td>
              <td>
                <DispatchNotificationDecisionBadge decision={decision.decision}/>
              </td>
              <td>
                <Text size="sm">
                  {decision.skipReason ? (
                    <DispatchNotificationSkipReason code={decision.skipReason}/>
                  ) : (
                    <>-</>
                  )}
                </Text>
              </td>
              <td>
                <Text size="sm" color="dimmed">
                  <FormattedDateTime date={decision.nextAllowedAtDttm}/>
                </Text>
              </td>
            </tr>
          ))}
        </>
      ) : (
        <tr>
          <td colSpan={4}>
            <Text size="sm" color="dimmed">
              {t("common.entity.dispatchnotification.modal.preview.empty", "No previews found.")}
            </Text>
          </td>
        </tr>
      )}
      </tbody>
    </Table>
  );
}

function BatchResultsTable({result}: { result: DispatchNotificationBatchCreateResponse }) {
  const {t} = useTranslation(["common"]);

  return (
    <Table withBorder highlightOnHover>
      <thead>
      <tr>
        <th>{t("common.entity.dispatchnotification.driver", "Driver")}</th>
        <th>{t("common.entity.dispatchnotification.status", "Status")}</th>
        <th>{t("common.entity.dispatchnotification.reason", "Reason")}</th>
        <th>{t("common.entity.dispatchnotification.notification", "Notification")}</th>
      </tr>
      </thead>
      <tbody>
      {result && result.results.length > 0 ? (
        <>
          {result.results.map(({dispatchId, result}, i) => (
            <tr key={`${dispatchId}-${i}`}>
              <td>
                <Text size="sm">
                  {result.driverId ? <DefaultText value={result.driverName}/> : "-"}
                </Text>
              </td>
              <td>
                {result.status ? (
                  <DispatchNotificationStatusBadge code={result.status}/>
                ) : (
                  <>-</>
                )}
              </td>
              <td>
                <Text size="sm">
                  {result.skipReason ? (
                    <DispatchNotificationSkipReason code={result.skipReason}/>
                  ) : (
                    <>-</>
                  )}
                </Text>
              </td>
              <td>
                <DispatchNotificationIcon notificationId={result.notificationId} statusCode={result.status}/>
              </td>
            </tr>
          ))}
        </>
      ) : (
        <tr>
          <td colSpan={4}>
            <Text size="sm" color="dimmed">
              {t("common.entity.dispatchnotification.modal.results.empty", "No results.")}
            </Text>
          </td>
        </tr>
      )}
      </tbody>
    </Table>
  );
}
